import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/AuthModal';
import './BenefitsPage.css';

// Clean, modern outline icons (not emoji) — consistent stroke weight and
// size across all six boxes, matching the style used on the Businesses page.
function IconDiscount() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M11 3.5H6.5a2 2 0 00-1.4.6L3.6 5.6a2 2 0 000 2.8l9 9a2 2 0 002.8 0l6-6a2 2 0 000-2.8l-9-9a2 2 0 00-1.4-.6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <circle cx="8.5" cy="8.5" r="1.4" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );
}
function IconLocation() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M12 2c-4.4 0-8 3.6-8 8 0 6 8 12 8 12s8-6 8-12c0-4.4-3.6-8-8-8z" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
  );
}
function IconInstant() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round"/>
    </svg>
  );
}
function IconVerified() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3l7 3v5.5c0 4.6-3 8.7-7 9.9-4-1.2-7-5.3-7-9.9V6l7-3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
      <path d="M8.5 12l2.4 2.4L15.5 9.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function IconNoFees() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M8.5 12.5l2.2 2.2 4.3-4.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function IconExpanding() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 16l6-6 4 4 8-9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M15 5h6v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const perks = [
  {
    Icon: IconDiscount,
    title: 'Exclusive Discounts',
    desc: 'Save money at every Volcyy partner restaurant worldwide, every visit, every time.',
  },
  {
    Icon: IconLocation,
    title: 'Find Restaurants Near You',
    desc: 'Find partner restaurants near you with one tap using Volcyy\'s location feature.',
  },
  {
    Icon: IconInstant,
    title: 'Instant Access',
    desc: 'Start saving money as soon as you join. No waiting required.',
  },
  {
    Icon: IconVerified,
    title: 'Verified Membership',
    desc: 'Staff can scan your QR code instantly to confirm your active membership.',
  },
  {
    Icon: IconNoFees,
    title: 'No Hidden Fees',
    desc: 'Joining Volcyy is completely free. Upgrade to Volcyy Black anytime for bigger discounts.',
  },
  {
    Icon: IconExpanding,
    title: 'Always Expanding',
    desc: 'New restaurant partners added regularly worldwide.',
  },
];

const FREE_BULLETS = [
  'Discounts at every partner restaurant',
  'Save every time you eat out',
  'Discover restaurants near you',
];
const BLACK_BULLETS = [
  'Bigger discounts at every partner restaurant',
  'Save more money every time you eat out',
  'Get the highest available discounts',
];

export default function BenefitsPage() {
  const { user, isBlack } = useAuth();
  const navigate = useNavigate();
  const [authModal, setAuthModal] = useState(null);
  const [authIntent, setAuthIntent] = useState('free'); // where to send them after signing up

  function handleFreeCta() {
    if (!user) {
      setAuthIntent('free');
      setAuthModal('signup');
      return;
    }
    navigate('/');
  }

  function handleBlackCta() {
    if (isBlack) {
      navigate('/');
      return;
    }
    if (!user) {
      setAuthIntent('black');
      setAuthModal('signup');
      return;
    }
    navigate('/checkout', { state: { from: '/benefits' } });
  }

  function handleBottomCta() {
    document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <>
      <main className="benefits-page">
        <section className="benefits-hero">
          <div className="benefits-hero__inner">
            <h1 className="benefits-hero__title">Free to join.<br /><span>Endless savings.</span></h1>
            <p className="benefits-hero__sub">
              Join thousands of members worldwide who save money every time they eat out.
            </p>
          </div>
        </section>

        <section className="benefits-pricing" id="pricing-section">
          <div className="benefits-pricing__inner benefits-pricing__inner--grid">
            <div className="benefits-pricing__card">
              <div className="benefits-pricing__badge">Volcyy</div>
              <div className="benefits-pricing__price">
                <span className="benefits-pricing__amount">$0</span>
              </div>
              <p className="benefits-pricing__note">Free forever. No credit card required.</p>

              <ul className="benefits-pricing__list">
                {FREE_BULLETS.map(b => (
                  <li key={b}>
                    <span className="benefits-pricing__check" aria-hidden="true">✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <button className="benefits-pricing__cta" onClick={handleFreeCta}>
                {user ? 'Start Browsing' : 'Join Free'}
              </button>
            </div>

            <div className="benefits-pricing__card benefits-pricing__card--black">
              <div className="benefits-pricing__badge benefits-pricing__badge--black">Volcyy Black</div>
              <div className="benefits-pricing__price">
                <span className="benefits-pricing__amount">$1.99</span>
                <span className="benefits-pricing__period">/ month</span>
              </div>
              <p className="benefits-pricing__note">Cancel anytime.</p>

              <ul className="benefits-pricing__list">
                {BLACK_BULLETS.map(b => (
                  <li key={b}>
                    <span className="benefits-pricing__check" aria-hidden="true">✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <button className="benefits-pricing__cta benefits-pricing__cta--black" onClick={handleBlackCta}>
                {isBlack ? 'Browse Restaurants' : 'Upgrade to Black'}
              </button>
            </div>
          </div>
        </section>

        <section className="benefits-perks">
          <div className="benefits-perks__inner">
            <h2 className="benefits-perks__heading">Everything Included</h2>
            <div className="benefits-perks__grid">
              {perks.map(p => (
                <div key={p.title} className="benefits-perk">
                  <span className="benefits-perk__icon"><p.Icon /></span>
                  <h3 className="benefits-perk__title">{p.title}</h3>
                  <p className="benefits-perk__desc">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="benefits-cta">
          <div className="benefits-cta__inner">
            <h2>Ready to start saving?</h2>
            <p>Join Volcyy today and enjoy exclusive discounts at the best restaurants worldwide.</p>
            <button className="benefits-cta__btn" onClick={handleBottomCta}>
              See Plans
            </button>
          </div>
        </section>
      </main>

      {authModal && (
        <AuthModal
          defaultTab={authModal}
          onClose={() => setAuthModal(null)}
          onSuccess={() =>
            authIntent === 'black'
              ? navigate('/checkout', { state: { from: '/benefits' } })
              : navigate('/')
          }
        />
      )}
    </>
  );
}
